import React, { Component } from 'react';
import PropTypes from 'prop-types';
import OutsideClickHandler from 'react-outside-click-handler';
import momentPropTypes from 'react-moment-proptypes';
import moment from 'moment';
import nullable from '../../utils/nullable';

const parseValue = value => {
  if (value === null || value === undefined) return { day: null, month: 0 };
  if (value instanceof Date) return { day: value.getDate(), month: value.getMonth() };
  if (typeof value.day === 'number' && typeof value.month === 'number')
    return { day: value.day, month: value.month };
  if (value instanceof moment) return { day: value.date(), month: value.month() };

  return { day: null, month: 0 };
};
export const displayDayMonth = (value, monthFormat = 'MMMM') => {
  const date = parseValue(value);

  return (
    <span>
      {`${date.day}`}
      &nbsp;
      {moment({ year: 2000, month: date.month, day: 1 }).format(monthFormat)}
    </span>
  );
};
class DayMonthPicker extends Component {
  constructor(props) {
    super(props);
    const { value } = props;
    const { day, month } = parseValue(value);
    this.state = {
      day,
      month,
    };
  }
  submitValue = (force = false) => {
    const { day, month } = this.state;
    const { outputType, onChange, value } = this.props;
    const oldValue = parseValue(value);
    if (Number.isNaN(day) || day === null) {
      if (force || value) onChange(null);
      return;
    }
    const daysInMonth = new Date(2000, month + 1, 0).getDate();
    const safeDay = Math.min(day, daysInMonth);

    if (!force) {
      if (oldValue.day === safeDay && oldValue.month === month) return;
    }

    if (outputType === 'object') onChange({ day: safeDay, month });
    else if (outputType === 'date') onChange(new Date(2000, month, safeDay));
    else if (outputType === 'moment') onChange(moment.utc({ year: 2000, month, day: safeDay }));
  };

  componentDidUpdate = ({ value: oldValue }) => {
    const { value: newValue } = this.props;

    const { oldDay, oldMonth } = parseValue(oldValue);
    const { newDay, newMonth } = parseValue(newValue);
    if (oldDay !== newDay || oldMonth !== newMonth) {
      this.setState({
        day: newDay,
        month: newMonth,
      });
    }
  };

  createMonthOptions = monthFormat => {
    const m = [];
    for (let i = 0; i < 12; i += 1)
      m.push(
        <option key={i} value={i}>
          {moment({ year: 2000, month: i, day: 1 }).format(monthFormat)}
        </option>,
      );

    return m;
  };

  handleOutsideClick = () => {
    const { onBlur } = this.props;
    this.submitValue();
    onBlur();
  };

  changeDay = e => {
    let day = parseInt(e.target.value, 10) || null;
    const { month } = this.state;
    if (day) {
      const daysInMonth = new Date(2000, month + 1, 0).getDate();
      day = Math.min(day, daysInMonth);
    }

    this.setState({ day }, () => {
      this.submitValue();
    });
  };
  changeMonth = e => {
    let { day } = this.state;
    const month = parseInt(e.target.value, 10) || 0;
    const daysInMonth = new Date(2000, month + 1, 0).getDate();
    day = Math.min(day || 1, daysInMonth);
    this.setState({ day, month }, () => {
      this.submitValue();
    });
  };

  render() {
    const { inputRef, onKeyDown, monthFormat, disabled } = this.props;
    const { day, month } = this.state;

    const daysInMonth = new Date(2000, month + 1, 0).getDate();

    return (
      <OutsideClickHandler onOutsideClick={this.handleOutsideClick}>
        <div className="react-day-month-picker">
          <input
            type="number"
            className="form-control day-input"
            value={day === null ? '' : day}
            min="1"
            max={daysInMonth}
            onChange={this.changeDay}
            ref={inputRef}
            onKeyDown={onKeyDown}
            disabled={disabled}
            name="day"
          />
          <select
            value={month}
            className="form-control month-input"
            onChange={this.changeMonth}
            onKeyDown={onKeyDown}
            disabled={disabled}
            ref={e => {
              this.monthInput = e;
            }}
            name="month"
          >
            {this.createMonthOptions(monthFormat)}
          </select>
        </div>
      </OutsideClickHandler>
    );
  }
}

DayMonthPicker.propTypes = {
  value: nullable(
    PropTypes.oneOfType([
      PropTypes.instanceOf(Date),
      momentPropTypes.momentObj,
      PropTypes.shape({ day: PropTypes.number, month: PropTypes.number }),
    ]),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func,
  onBlur: PropTypes.func,
  inputRef: PropTypes.func,
  outputType: PropTypes.oneOf(['moment', 'date', 'object']),
  monthFormat: PropTypes.oneOf(['M', 'MM', 'MMM', 'MMMM']),
  disabled: PropTypes.bool,
};

DayMonthPicker.defaultProps = {
  outputType: 'date',
  onKeyDown: () => {},
  inputRef: () => {},
  onBlur: () => {},
  monthFormat: 'MMMM',
  disabled: false,
};

export default DayMonthPicker;
